/**
 * BookingHubPage — CWR 1.3 Home body (Frame 1 / Frame 2 reskin).
 *
 * Ported VERBATIM from the mockup CSS, substituting only the PROMOTED semantic
 * chrome tokens (packages/shared/src/theme/tokens.ts → presets.ts) for the raw
 * mockup deps so light-mode (terracotta) stays correct. Static design constants
 * (radii, fonts, the mockup's exact px values + negative tracking) are copied
 * as-is — they are not themable.
 *
 * Source of truth: docs/mockups/client-dashboard-redesign/dashboard-mockup.html
 *   section :744 · shared glass ring :778 · hero :768 · book card :953 ·
 *   quick actions :1102 · booking card :1165 · happening :1235 · about :1367.
 */

.page {
  /* Static (non-themable) design constants — mirror the mockup :root. */
  --r-sm: 10px;
  --r-md: 14px;
  --r-lg: 18px;
  --r-pill: 9999px;
  --font-body: var(--client-font-body, 'Outfit', system-ui, -apple-system, sans-serif);
  --font-display: var(--client-font-heading, 'EB Garamond', Georgia, serif);

  max-width: 480px;
  margin: 0 auto;
  /* One horizontal inset for the whole feed so every top-level card is the SAME
     width (avoids per-element margins + WKWebView <button> shrink-to-fit). */
  padding: 0 20px;
}

/* ── Section scaffolding (mockup :744) ───────────────────────────────── */
.section {
  margin-bottom: 14px;
}
.sectionHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}
.sectionTitle {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.022em;
  color: var(--text-primary, #faf3e8);
}
.sectionLink {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: var(--accent, #e8954f);
}
.sectionLink svg {
  width: 12px;
  height: 12px;
  stroke-width: 2.2;
}

/* ── Shared tapered glass ring (mockup :778) ─────────────────────────── */
.nextSession::before,
.quickAction::before,
.happeningCard::before,
.aboutCard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: var(--booking-card-ring-bg, linear-gradient(135deg, rgba(255, 181, 133, 0.16) 0%, rgba(255, 181, 133, 0.03) 100%));
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}

/* ════════════════════════════════════════════════════════════════════
   Hero — Your Next Session (mockup :768)
   ════════════════════════════════════════════════════════════════════ */
.nextSession {
  position: relative;
  display: block;
  width: 100%;
  margin: 0 0 14px;
  padding: 13px 14px 12px;
  text-align: left;
  appearance: none;
  border: 0;
  cursor: pointer;
  border-radius: var(--r-lg);
  background: var(--glass-bg-strong, rgba(24, 22, 20, 0.72));
  backdrop-filter: var(--glass-blur, blur(22px) saturate(140%));
  box-shadow: var(--booking-card-shadow, 0 8px 24px rgba(0, 0, 0, 0.45));
  overflow: hidden;
  color: var(--text-primary, #faf3e8);
}
.nextSession:active {
  transform: scale(0.99);
}
.nextSessionTop {
  display: grid;
  grid-template-columns: 50px 1fr auto;
  gap: 12px;
  align-items: stretch;
}
.sessionIcon {
  width: 50px;
  height: 50px;
  align-self: center;
  border-radius: 50%;
  background: var(--session-icon-bg, radial-gradient(circle at 30% 28%, rgba(246, 146, 88, 0.32) 0%, rgba(184, 85, 21, 0.16) 100%));
  border: 1px solid var(--session-icon-border, rgba(233, 122, 61, 0.22));
  display: grid;
  place-items: center;
  color: var(--session-icon-glyph, #e97a3d);
}
.sessionIcon svg {
  width: 22px;
  height: 22px;
  stroke-width: 1.7;
}
.sessionMeta {
  align-self: center;
  min-width: 0;
}
.sessionKicker {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--accent, #e8954f);
  margin-bottom: 2px;
}
.sessionTitle {
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.018em;
  color: var(--text-primary, #faf3e8);
  line-height: 1.1;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sessionDetailRow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--text-secondary);
}
.sessionDetailRow > span {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.sessionDetailRow svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
  color: var(--text-tertiary);
}
.nextSessionSide {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  padding: 2px 0 4px;
}
.chev {
  width: 14px;
  height: 14px;
  color: var(--chevron-color);
  stroke-width: 2.4;
}
.statusPill {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: var(--r-pill);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.005em;
  white-space: nowrap;
  background: var(--status-pill-bg, rgba(233, 122, 61, 0.13));
  color: var(--status-pill-text, #f69258);
}

/* capacity block (mockup :917) */
.capacityBlock {
  margin-top: 10px;
  padding: 9px 12px 10px;
  background: var(--capacity-block-bg, rgba(24, 22, 20, 0.72));
  border: 1px solid var(--capacity-block-border, rgba(255, 255, 255, 0.05));
  border-radius: var(--r-sm);
  box-shadow: var(--capacity-block-shadow, 0 2px 6px rgba(0, 0, 0, 0.35));
}
.capacityRow {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.capacityLabel {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 400;
  color: var(--text-secondary);
}
.capacityLabel svg {
  width: 14px;
  height: 14px;
  stroke-width: 1.8;
  color: var(--accent, #e8954f);
}
.capacityBar {
  height: 6px;
  background: var(--capacity-track-bg);
  border-radius: var(--r-pill);
  overflow: hidden;
}
.capacityFill {
  height: 100%;
  background: var(--capacity-fill-bg, linear-gradient(90deg, #b85515 0%, #e97a3d 100%));
  border-radius: var(--r-pill);
}

/* 1:1 fallback (provider + location) — same container as the capacity block */
.oneOnOne {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 9px 12px 10px;
  background: var(--capacity-block-bg, rgba(24, 22, 20, 0.72));
  border: 1px solid var(--capacity-block-border, rgba(255, 255, 255, 0.05));
  border-radius: var(--r-sm);
  box-shadow: var(--capacity-block-shadow, 0 2px 6px rgba(0, 0, 0, 0.35));
}
.oneOnOneText {
  min-width: 0;
}
.oneOnOneName {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary, #faf3e8);
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.oneOnOneLocation {
  font-size: 11px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ════════════════════════════════════════════════════════════════════
   Hero — Book a Session (mockup :953)
   ════════════════════════════════════════════════════════════════════ */
.bookCard {
  position: relative;
  display: block;
  width: 100%;
  margin: 4px 0 14px;
  padding: 14px 16px;
  appearance: none;
  border: 0;
  cursor: pointer;
  border-radius: var(--r-lg);
  background: var(--book-card-bg, rgba(24, 22, 20, 0.72));
  backdrop-filter: var(--glass-blur, blur(22px) saturate(140%));
  box-shadow: var(--book-card-shadow, 0 8px 24px rgba(0, 0, 0, 0.45));
  overflow: hidden;
  color: var(--text-primary, #faf3e8);
}
.bookCard:active {
  transform: scale(0.99);
}
/* uniform amber ring (mockup :803) */
.bookCard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 0.5px;
  background: var(--book-card-ring-bg, rgba(233, 122, 61, 0.35));
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
/* diagonal amber bloom (mockup :966) */
.bookCard::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--book-card-bloom-bg, none);
  pointer-events: none;
  z-index: 1;
}
.bookCardInner {
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px;
  align-items: center;
}
.bookIcon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--book-icon-bg, radial-gradient(circle at 30% 28%, #ffb585 0%, #e97a3d 45%, #b85515 100%));
  border: var(--book-icon-border, 1.5px solid rgba(255, 220, 180, 0.3));
  box-shadow: var(--book-icon-shadow, 0 0 28px rgba(233, 122, 61, 0.38));
  display: grid;
  place-items: center;
  color: var(--text-on-accent);
  flex-shrink: 0;
}
.bookIcon svg {
  width: 26px;
  height: 26px;
  stroke-width: 1.8;
}
.bookText {
  min-width: 0;
  text-align: left;
  color: var(--text-primary, #faf3e8);
}
.bookKicker {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent, #e8954f);
  margin-bottom: 4px;
}
.bookTitle {
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.022em;
  line-height: 1.1;
  margin-bottom: 3px;
}
.bookSubtitle {
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.35;
  color: var(--text-secondary);
}
.bookArrow {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--accent-rgb, 233, 122, 61), 0.55);
  background: var(--book-arrow-bg, rgba(34, 32, 28, 0.65));
  backdrop-filter: blur(8px);
  color: var(--accent, #e8954f);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.bookArrow svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}
/* empty-state hero variant (mockup :1075) */
.bookCardHero {
  padding: 18px 18px 17px;
}
.bookCardHero .bookCardInner {
  gap: 16px;
}
.bookCardHero .bookIcon {
  width: 64px;
  height: 64px;
}
.bookCardHero .bookIcon svg {
  width: 28px;
  height: 28px;
}
.bookCardHero .bookTitle {
  font-size: 24px;
  margin-bottom: 4px;
}

/* ════════════════════════════════════════════════════════════════════
   Quick Actions (mockup :1102) — horizontal icon-left chips
   ════════════════════════════════════════════════════════════════════ */
.quickActions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}
.quickAction {
  position: relative;
  appearance: none;
  border: 0;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 7px;
  padding: 10px 9px;
  border-radius: var(--r-md);
  background: var(--quick-action-1-bg, rgba(22, 20, 19, 0.6));
  backdrop-filter: var(--glass-blur, blur(22px) saturate(140%));
  box-shadow: var(--glass-shadow, 0 8px 24px rgba(0, 0, 0, 0.45));
  text-align: left;
  overflow: hidden;
}
.quickAction:nth-child(2) {
  background: var(--quick-action-2-bg, rgba(22, 20, 19, 0.6));
}
.quickAction:nth-child(3) {
  background: var(--quick-action-3-bg, rgba(22, 20, 19, 0.6));
}
.quickAction:active {
  transform: scale(0.97);
}
.quickActionIcon {
  display: grid;
  place-items: center;
  color: var(--quick-action-icon-color, var(--accent, #e8954f));
  flex-shrink: 0;
}
.quickActionIcon svg {
  width: 16px;
  height: 16px;
  stroke-width: 1.8;
}
.quickActionLabel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.quickActionTitle {
  font-family: var(--font-body);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.15;
  white-space: nowrap;
  color: var(--text-primary, #faf3e8);
}
.quickActionSub {
  font-family: var(--font-body);
  font-size: 10.5px;
  font-weight: 400;
  letter-spacing: 0.005em;
  line-height: 1.3;
  margin-top: 1px;
  color: var(--text-tertiary);
}

/* client-chat-screen A2 (client-chat-door.html :1687, :1708): the Chat chip carries the unread
   secure count in its corner. Solid accent, cream digits; the chip clips its overflow, so the
   badge sits inside the corner. No ring: an opaque ring in --bg-card showed as a dark outline on
   translucent (glass) chips in tenant themes (Gabriel 2026-10-07). */
.quickActionBadge {
  position: absolute;
  top: 5px;
  right: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9999px;
  background: var(--accent);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 10.5px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  letter-spacing: 0;
  z-index: 3;
}
.quickActionChat .quickActionLabel { min-width: 0; }
/* A2 as drawn; "Secure chat" fits whole in equal thirds from 375px up (measured 2026-10-05). */
.quickActionChat .quickActionSub { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Upcoming Booking card (mockup :1165) extracted to the shared
   components/client/BookingCard (rd-bookings) — Home renders it via UpcomingList. */

/* ════════════════════════════════════════════════════════════════════
   Happening Soon (mockup :1235) — 148px image cards, glass ring only
   ════════════════════════════════════════════════════════════════════ */
/* At rest the first card's left edge lines up with every other card + the
   section title (scroll-padding keeps the snap on the page's 20px inset). */
.happening {
  margin-bottom: 14px;
}
.happeningHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}
/* A side-scroller clips everything past its box, card shadows included, which
   cut them into a hard-edged dark box on non-black looks. The strip runs to the
   screen edges with room above and below for the shadow; the negative margins
   give that room back so the layout is unchanged. */
.happeningScroll {
  display: flex;
  gap: 12px;
  margin: -10px -20px -36px;
  padding: 10px 20px 36px;
  scroll-padding-inline: 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}
.happeningScroll::-webkit-scrollbar {
  display: none;
}
.happeningCard {
  position: relative;
  /* Stack image + body as flex items — a <button> (vs the mockup's <div>) otherwise
     leaves a vertical gap around its content box (the "gap above/below the image"). */
  display: flex;
  flex-direction: column;
  flex: 0 0 148px;
  scroll-snap-align: start;
  appearance: none;
  border: 0;
  cursor: pointer;
  padding: 0;
  border-radius: var(--r-md);
  background: var(--glass-bg-strong, rgba(24, 22, 20, 0.72));
  backdrop-filter: var(--glass-blur, blur(22px) saturate(140%));
  box-shadow: var(--happening-card-shadow, 0 8px 24px rgba(0, 0, 0, 0.45));
  overflow: hidden;
  text-align: left;
}
.happeningCard:active {
  transform: scale(0.99);
}
.happeningImage {
  position: relative;
  width: 100%;
  height: 92px;
  background-size: cover;
  background-position: center;
  background-color: var(--bg-card-2, #221810);
}
.happeningImage::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.05) 50%, rgba(20, 14, 10, 0.7) 100%);
}
.spotsPill {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 4px 9px;
  border-radius: var(--r-pill);
  font-family: var(--font-body);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: -0.005em;
  background: var(--spots-pill-bg, rgba(233, 122, 61, 0.92));
  color: var(--spots-pill-text, #1a0e05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
}
.spotsPill[data-variant='low'] {
  background: var(--spots-pill-low-bg, rgba(246, 146, 88, 0.95));
}
.spotsPill[data-variant='sold'] {
  background: var(--glass-bg-strong, rgba(24, 22, 20, 0.72));
  color: var(--text-tertiary);
}
.happeningBody {
  padding: 9px 11px 11px;
}
.happeningTitle {
  margin: 0 0 5px;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.022em;
  color: var(--text-primary, #faf3e8);
  line-height: 1.2;
}
.happeningMetaRow {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  letter-spacing: -0.005em;
  color: var(--text-tertiary);
  margin-bottom: 1px;
}
.happeningMetaRow svg {
  width: 10px;
  height: 10px;
  stroke-width: 1.8;
  flex-shrink: 0;
}

/* ════════════════════════════════════════════════════════════════════
   About the Studio (mockup about-page.html Frame 1) — editorial closer.
   The WHOLE card is one interactive control (button) → /about (§5.1 / D9).
   ════════════════════════════════════════════════════════════════════ */
.aboutCard {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin: 16px 0 0;
  padding: 14px 16px;
  border: none;
  border-radius: var(--r-lg);
  background: var(--glass-bg-strong, rgba(24, 22, 20, 0.72));
  backdrop-filter: var(--glass-blur, blur(22px) saturate(140%));
  box-shadow: var(--about-card-shadow, 0 8px 24px rgba(0, 0, 0, 0.45));
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  -webkit-tap-highlight-color: transparent;
}
.aboutPhoto {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
}
.aboutPhotoFallback {
  background-image: radial-gradient(circle at 30% 28%, var(--accent-hot, #f69258) 0%, var(--accent-deep, #b85515) 100%);
}
.aboutText {
  flex: 1;
  min-width: 0;
}
.aboutKicker {
  display: block; /* span inside the card <button> (§5.1 phrasing-content fix) */
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--accent, #e8954f);
  margin-bottom: 3px;
}
.aboutName {
  display: block; /* span inside the card <button> (§5.1 phrasing-content fix) */
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 18px;
  letter-spacing: -0.018em;
  color: var(--text-primary, #faf3e8);
  line-height: 1.15;
  margin-bottom: 4px;
}
.aboutBio {
  display: block; /* span inside the card <button> (§5.1 phrasing-content fix) */
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.42;
  color: var(--text-secondary);
}
/* Read-more affordance — a styled NON-focusable element inside the card button
   (mockup .aboutReadmore, retoned to --accent). Never a nested button (§5.1 / D9). */
.aboutReadmore {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 8px;
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: var(--accent); /* no hex fallback — the engine always injects; pre-existing fallbacks in this file belong to b-cwr-hardcoded-theme-literals */
}
.aboutReadmore svg {
  width: 13px;
  height: 13px;
  stroke-width: 2.2;
}

/* ── Skeleton (retained; shell owns the live loading state) ──────────── */
.loadingState {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}
.loadingSpinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--border-soft);
  border-top-color: var(--accent, #e8954f);
  animation: booking-hub-spin 0.8s linear infinite;
}
@keyframes booking-hub-spin {
  to {
    transform: rotate(360deg);
  }
}
